import React from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { ChevronBack } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { InlineLinkButton } from '@apps/acquisitionFunnels/createWedding/components/InlineLinkButton/InlineLinkButton';

type WebsiteV2ChoiceOverlayProps = Readonly<{
  /** Pilot arm: 'ai' pitches the design-it-around-your-story path; 'templates' pitches
   *  all-new designer templates + the new editor (no AI mention). */
  variant: 'ai' | 'templates';
  /** Opt into the WebsiteV2 beta: create the event flagged, then pick a template / AI design. */
  onChooseBeta: () => void;
  /** "Skip" — no decision made; defaults into the beta path (recorded separately). */
  onSkip: () => void;
  /** The footnote path: create the event exactly as before. */
  onChooseClassic: () => void;
  /** Back out to the form without creating anything. */
  onDismiss: () => void;
}>;

// Decorative palette dots — the same swatches the brief composer offers next, so the tease
// pays off on the following screen.
const PALETTE_DOTS = ['#9CAF88', '#C1683C', '#7C9CB8', '#F4C2C2', '#B57EDC'] as const;

// Matches MoodPreview's "classic" font mood — deliberately a system serif stack.
const SERIF_STACK = 'Georgia, "Times New Roman", serif';

/**
 * Full-screen moment after the create form validates and before the event is created: the new
 * website builder is the hero (the choice decides whether `useWebsiteV2` rides the create
 * payload, so it must precede the mutation); the classic experience is a footnote, and Skip
 * simply takes the default — the beta.
 */
export const WebsiteV2ChoiceOverlay: React.FC<WebsiteV2ChoiceOverlayProps> = ({ variant, onChooseBeta, onSkip, onChooseClassic, onDismiss }) => {
  const { tq } = useTranslation('createWedding');

  const isAiVariant = variant === 'ai';
  const headline = isAiVariant ? tq`A website designed just for the two of you` : tq`A beautiful new website for the two of you`;
  const body = isAiVariant
    ? tq`Tell us your story and we'll design a site with as much personality as you. Or start from a designer template and make it yours.`
    : tq`Choose from our selection of professionally designed templates.`;
  const cta = isAiVariant ? tq`Design my website` : tq`Choose my design`;

  return (
    <Flex
      position="fixed"
      top={0}
      right={0}
      bottom={0}
      left={0}
      zIndex="modal"
      flexDirection="column"
      overflowY="auto"
      background="radial-gradient(70% 55% at 12% 0%, #FBEDE4 0%, rgba(251, 237, 228, 0) 60%),
        radial-gradient(60% 50% at 92% 8%, #EFEAFB 0%, rgba(239, 234, 251, 0) 55%),
        radial-gradient(70% 60% at 50% 105%, #EAF2EC 0%, rgba(234, 242, 236, 0) 55%), #FFFFFF"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        paddingX={6}
        paddingY={5}
      >
        <ButtonV2
          variant="ghost"
          intent="neutral"
          startIcon={() => <ChevronBack size={16} />}
          onClick={onDismiss}
        >
          {tq`Back`}
        </ButtonV2>
        <ButtonV2
          variant="ghost"
          intent="neutral"
          onClick={onSkip}
        >
          {tq`Skip`}
        </ButtonV2>
      </Flex>
      <Flex
        flex={1}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        rowGap={7}
        paddingX={{ _: 5, sm2: 7 }}
        paddingBottom={9}
      >
        {/* Gradient-framed hero card — the pastel frame stands in for imagery until real assets exist. */}
        <Box
          borderRadius={pxToRem(28)}
          padding={pxToRem(8)}
          width="100%"
          maxWidth={pxToRem(640)}
          background="linear-gradient(120deg, #F6D8C8 0%, #E8DFF8 42%, #F9E9DC 75%, #DCE9E0 100%)"
          boxShadow="0px 24px 60px rgba(60, 45, 30, 0.10)"
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            rowGap={5}
            borderRadius={pxToRem(22)}
            backgroundColor="white"
            paddingX={{ _: 6, sm2: 9 }}
            paddingY={9}
            textAlign="center"
          >
            <TextV2
              typographyVariant="label3"
              color="mono10"
              letterSpacing="0.16em"
            >
              {tq`NEW · BETA`}
            </TextV2>
            <Box
              as="h2"
              fontFamily={SERIF_STACK}
              fontWeight={400}
              fontSize={{ _: pxToRem(34), sm2: pxToRem(46) }}
              lineHeight={1.12}
              color="mono14"
              maxWidth={pxToRem(520)}
            >
              {headline}
            </Box>
            <TextV2
              typographyVariant="body1"
              color="mono12"
              maxWidth={pxToRem(440)}
            >
              {body}
            </TextV2>
            <Flex
              columnGap={3}
              alignItems="center"
              paddingY={2}
              aria-hidden
            >
              {PALETTE_DOTS.map(hex => (
                <Box
                  key={hex}
                  width={pxToRem(14)}
                  height={pxToRem(14)}
                  borderRadius="full"
                  backgroundColor={hex}
                />
              ))}
              <Box
                width="1px"
                height={pxToRem(16)}
                backgroundColor="mono4"
                marginX={2}
              />
              <Box
                fontFamily={SERIF_STACK}
                fontStyle="italic"
                fontSize={pxToRem(17)}
                color="mono10"
              >
                Aa
              </Box>
              <Box
                fontFamily='-apple-system, "Helvetica Neue", Arial, sans-serif'
                fontWeight={300}
                letterSpacing="0.1em"
                fontSize={pxToRem(15)}
                color="mono10"
              >
                AA
              </Box>
            </Flex>
            <ButtonV2
              intent="primary"
              size="lg"
              minWidth={pxToRem(240)}
              onClick={onChooseBeta}
            >
              {cta}
            </ButtonV2>
          </Flex>
        </Box>
        <Flex
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          columnGap={1}
          typographyVariant="body2"
          color="mono10"
        >
          <TextV2
            as="span"
            typographyVariant="body2"
            color="mono10"
          >
            {tq`Prefer the website builder you know?`}
          </TextV2>
          <InlineLinkButton onClick={onChooseClassic}>{tq`Use the classic Joy website`}</InlineLinkButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

WebsiteV2ChoiceOverlay.displayName = 'WebsiteV2ChoiceOverlay';
